import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

const META_ENV = ((import.meta as unknown as { env?: Record<string, string | undefined> }).env) || {};
// Build-time key (optional). Preferred when present; otherwise the key is fetched at
// runtime from /api/public/config so the committed build never has to embed it.
const BUILD_SITE_KEY = META_ENV.VITE_TURNSTILE_SITE_KEY || '';
const IS_DEV = Boolean(META_ENV.DEV);
// Cloudflare's TEST site key renders a permanent "For testing only / report to site owner"
// banner. When that's the configured key we don't render the widget at all — the matching
// TEST secret on the server skips verification, so forms still submit. A REAL key renders
// the widget and enforcement turns back on with no code changes.
const TEST_SITE_KEY = '1x00000000000000000000AA';
const isRealKey = (k: string) => Boolean(k) && k !== TEST_SITE_KEY;

let scriptPromise: Promise<void> | null = null;
// Cache the runtime lookup across every Turnstile instance on the page (Register,
// FeedbackWidget, ContactForm) so /api/public/config is hit at most once per load.
let runtimeKeyPromise: Promise<string | null> | null = null;

function fetchRuntimeSiteKey(): Promise<string | null> {
  if (runtimeKeyPromise) return runtimeKeyPromise;
  runtimeKeyPromise = fetch('/api/public/config')
    .then((r) => (r.ok ? r.json() : null))
    .then((cfg: any) => (cfg && typeof cfg.turnstile_site_key === 'string' ? cfg.turnstile_site_key : null))
    .catch(() => null);
  return runtimeKeyPromise;
}

export default function Turnstile({ onVerify, onExpire }: {
  onVerify: (token: string) => void;
  onExpire?: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const [siteKey, setSiteKey] = useState<string>(BUILD_SITE_KEY);

  // No build-time key → resolve it from the backend at runtime.
  useEffect(() => {
    if (siteKey) return undefined;
    let cancelled = false;
    fetchRuntimeSiteKey().then((key) => {
      if (cancelled) return;
      if (key) {
        setSiteKey(key);
      } else if (IS_DEV) {
        console.error(
          '[Turnstile] No site key configured. Set TURNSTILE_SITE_KEY on the backend ' +
          '(exposed via /api/public/config) or VITE_TURNSTILE_SITE_KEY at build time — ' +
          'otherwise the anti-spam widget will not render and protected forms 400 with ' +
          '"Anti-spam verification required".'
        );
      }
    });
    return () => { cancelled = true; };
  }, [siteKey]);

  // Render the widget once a REAL key is available (test keys are intentionally not rendered).
  useEffect(() => {
    if (!isRealKey(siteKey) || !ref.current) return undefined;
    let cancelled = false;

    loadTurnstile().then(() => {
      if (cancelled || !ref.current || !window.turnstile || widgetId.current) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: onVerify,
        'expired-callback': onExpire,
        'error-callback': onExpire,
      });
    });

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [siteKey, onVerify, onExpire]);

  if (!isRealKey(siteKey)) return null;
  return <div ref={ref} className="min-h-[65px]" />;
}

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Turnstile failed to load')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile failed to load'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}
